import * as React from "react";
import styled, { css, useTheme } from "styled-components";
import { Record } from "immutable";

import { Task } from "@typings/booking";
import {
  AVAILABLE_COUNTRIES,
  AVAILABLE_LANGS,
  ORIGIN,
  WHATS_INCLUDED_TYPE,
} from "@typings/globals";

import { transition } from "@ui/helpers";
import { Margins } from "@ui/Atoms";
import { ModalContentWhatIsIncluded } from "@ui/Organisms";
import { Counter, InfoMessage } from "@ui/Molecules";
import { Bed } from "@ui/Assets/Symbolicons/Bed";
import { Shower } from "@ui/Assets/Symbolicons/Shower";
import { Caret } from "@ui/Assets/Symbolicons/Caret";

import { Markdown } from "@containers/Markdown";

import { useBreakpoint } from "@hooks/useBreakpoint";

interface CleaningValues {
  bathrooms: number;
  bedrooms: number;
}

interface Props {
  homeCleaningTask: Record<Task>;
  setTask: (params: Task) => void;
  cleaningValues: CleaningValues;
  setCleaningValues: (
    cleaningValues: CleaningValues,
    buttonLabel: string,
    buttonId?: string,
  ) => void;
  origin: ORIGIN;
  language: AVAILABLE_LANGS;
  country: AVAILABLE_COUNTRIES;
  hasWhatsIncluded?: boolean;
  translations: {
    bedroomsText: string;
    bedroomText: string;
    bathroomsText: string;
    bathroomText: string;
    recommendedTimeText: string;
    includedAreasText: string;
    checkIncludedText: string;
  };
}

const ModalContentCalculateTime = React.memo((props: Props) => {
  const [showIncluded, setShowIncluded] = React.useState<boolean>(false);
  const theme = useTheme();
  const { mobile } = useBreakpoint("v1");

  const bedroomText =
    props.cleaningValues.bedrooms > 1
      ? props.translations.bedroomsText
      : props.translations.bedroomText;

  const bathroomText =
    props.cleaningValues.bathrooms > 1
      ? props.translations.bathroomsText
      : props.translations.bathroomText;

  return (
    <Margins xs={["base", "base_x2", null, null]}>
      <React.Fragment>
        <ModalContent>
          <FlexWrapper>
            <OptionContainer>
              <div>
                <Bed variant="line" size={24} />
              </div>
              <Margins
                xs={[null, null, "base", "base"]}
                md={[null, null, "base_x4", "base_x4"]}
              >
                <div>
                  <Counter
                    inputId={"calc-time-bedrooms"}
                    maxValue={10}
                    value={props.cleaningValues.bedrooms}
                    filter={(val: string) => Math.trunc(Number(val))}
                    {...(mobile && { customSize: theme.margins.base_x5 })}
                    onChange={(bedrooms: number, action?: string) => {
                      props.setCleaningValues(
                        {
                          ...props.cleaningValues,
                          bedrooms,
                        },
                        bedroomText,
                        `bedrooms-${action}`,
                      );
                    }}
                  />
                </div>
              </Margins>
              <span>{bedroomText}</span>
            </OptionContainer>
            <OptionContainer>
              <Shower variant="line" size={24} />
              <Margins
                xs={[null, null, "base", "base"]}
                md={[null, null, "base_x4", "base_x4"]}
              >
                <div>
                  <Counter
                    inputId={"calc-time-bathrooms"}
                    maxValue={10}
                    value={props.cleaningValues.bathrooms}
                    filter={(val: string) => Math.trunc(Number(val))}
                    {...(mobile && { customSize: theme.margins.base_x5 })}
                    onChange={(bathrooms: number, action?: string) => {
                      props.setCleaningValues(
                        {
                          ...props.cleaningValues,
                          bathrooms,
                        },
                        bathroomText,
                        `bathrooms-${action}`,
                      );
                    }}
                  />
                </div>
              </Margins>
              <span>{bathroomText}</span>
            </OptionContainer>
          </FlexWrapper>
          <Recommendation>
            <div>
              <Markdown>{props.translations.recommendedTimeText}</Markdown>
            </div>
          </Recommendation>
          <Margins xs={[null, "base_x4", null, null]}>
            <div>
              <InfoMessage message={props.translations.includedAreasText} />
            </div>
          </Margins>
          {props.hasWhatsIncluded && (
            <WhatIsIncludedLink
              href="#"
              role="button"
              onClick={e => {
                e.preventDefault();
                setShowIncluded(!showIncluded);
              }}
            >
              <CaretWrapper isActive={showIncluded}>
                <Caret
                  variant="solid"
                  color={theme.palette.blue500}
                  size={24}
                />
              </CaretWrapper>
              {props.translations.checkIncludedText}
            </WhatIsIncludedLink>
          )}
        </ModalContent>
        {props.hasWhatsIncluded && showIncluded && (
          <ModalContentWhatIsIncluded
            contentType={WHATS_INCLUDED_TYPE.BOOKING}
            origin={props.origin}
            lang={props.language}
            country={props.country}
          />
        )}
      </React.Fragment>
    </Margins>
  );
});

export { ModalContentCalculateTime };

ModalContentCalculateTime.displayName = "ModalContentCalculateTime";

const CaretWrapper = styled.span<{ isActive: boolean }>`
  padding-right: ${props => props.theme.margins.half};

  svg {
    transition: ${transition("transform")};
  }

  ${({ isActive }) =>
    !isActive &&
    css`
      svg {
        transform: rotate(-90deg);
      }
    `}
`;

const ModalContent = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-top: ${props => props.theme.margins.base_x4};
  font-size: ${props => props.theme.fonts.sizes.body};
`;

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${props => props.theme.margins.base_x4};

  span {
    min-width: 80px;
  }
`;

const Recommendation = styled.div`
  text-align: center;
  font-size: ${props => props.theme.fonts.sizes.body};
  margin: ${props => props.theme.margins.base} 0;
`;

const WhatIsIncludedLink = styled.a`
  display: flex;
  align-items: center;
  margin-bottom: ${props => props.theme.margins.base_x3};
  font-size: ${props => props.theme.fonts.sizes.body2};
`;
